import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, Button, Tabs, Tab, Typography, TextField, InputAdornment, 
  Select, MenuItem, FormControl, InputLabel, Grid, Card, CardContent,
  CardActions, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert
} from '@mui/material';
import { 
  Add, Search, FilterList, Edit, Delete, Visibility, 
  Article, TrendingUp, Bookmark, Schedule, Movie, Tv, 
  AttachMoney, SportsBasketball, Computer 
} from '@mui/icons-material';
import NewsListView from './NewsListView';
import CreateNewsForm from './CreateNewsForm';
import EditNewsForm from './EditNewsForm';
import NewsPreview from './NewsPreview';
import newsOperations from '../../../services/newsApi';
import { formatDistanceToNow } from 'date-fns';

const AdminNewsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [mode, setMode] = useState('list');
  const [selectedNews, setSelectedNews] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [dayFilter, setDayFilter] = useState('all');

  const categoryTabs = [
    { label: 'All', value: 'all', icon: <Article /> },
    { label: 'Movies', value: 'movies', icon: <Movie /> },
    { label: 'TV Shows', value: 'tv_shows', icon: <Tv /> },
    { label: 'Finance', value: 'finance', icon: <AttachMoney /> },
    { label: 'Sports', value: 'sports', icon: <SportsBasketball /> },
    { label: 'Technology', value: 'technology', icon: <Computer /> },
  ];
  const [previewNews, setPreviewNews] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load news data
  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    setLoading(true);
    try {
      const result = await newsOperations.getAdminNewsList({ limit: 100 });
      if (result.success) {
        setNews(result.items || []);
      }
    } catch (error) {
      console.error('Failed to load news:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { label: 'All News', value: 'all', icon: <Article /> },
    { label: 'Published', value: 'published', icon: <Visibility /> },
    { label: 'Draft', value: 'draft', icon: <Schedule /> },
    { label: 'Featured', value: 'featured', icon: <TrendingUp /> },
  ];

  // Filter news based on search and filters
  const filteredNews = useMemo(() => {
    let filtered = news;

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'featured') {
        filtered = filtered.filter(item => item.is_featured);
      } else {
        filtered = filtered.filter(item => item.status === statusFilter);
      }
    }

    // Day filter
    if (dayFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dayFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'yesterday':
          filterDate.setDate(filterDate.getDate() - 1);
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(filterDate.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(filterDate.getMonth() - 1);
          break;
        default:
          break;
      }
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.published_at || item.createdAt);
        return itemDate >= filterDate;
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.entity_name?.toLowerCase().includes(query) ||
        item.author?.toLowerCase().includes(query)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(a.published_at || a.createdAt);
      const dateB = new Date(b.published_at || b.createdAt);
      
      if (sortBy === 'latest') {
        return dateB - dateA;
      } else if (sortBy === 'oldest') {
        return dateA - dateB;
      } else if (sortBy === 'views') {
        return (b.view_count || 0) - (a.view_count || 0);
      } else if (sortBy === 'title') {
        return (a.title || '').localeCompare(b.title || '');
      }
      return 0;
    });

    return filtered;
  }, [news, categoryFilter, statusFilter, dayFilter, searchQuery, sortBy]);

  const handleDelete = async (newsId) => {
    try {
      const result = await newsOperations.deleteNews(newsId);
      if (result.success) {
        setNews(prev => prev.filter(item => item.id !== newsId));
        setDeleteConfirm(null);
        alert('News deleted successfully!');
        loadNews(); // Reload the list
      } else {
        alert('Failed to delete news: ' + result.error);
      }
    } catch (err) {
      console.error('Failed to delete news:', err);
      alert('Failed to delete news: ' + err.message);
    }
  };

  const NewsCard = ({ article }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {article.featured_image && (
        <Box sx={{ height: 200, overflow: 'hidden' }}>
          <img 
            src={article.featured_image} 
            alt={article.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
          {article.category && (
            <Chip 
              label={article.category.replace('_', ' ').toUpperCase()} 
              color="secondary" 
              size="small" 
            />
          )}
          <Chip 
            label={article.status} 
            color={article.status === 'published' ? 'success' : 'default'} 
            size="small" 
          />
          {article.is_featured && <Chip label="Featured" color="primary" size="small" />}
          {article.is_breaking && <Chip label="Breaking" color="error" size="small" />}
        </Box>
        {article.entity_name && (
          <Typography variant="caption" color="primary" display="block" mb={1}>
            Related to: {article.entity_name}
          </Typography>
        )}
        <Typography variant="h6" gutterBottom sx={{ 
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {article.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          mb: 2
        }}>
          {article.description}
        </Typography>
        <Box display="flex" alignItems="center" gap={2} color="text.secondary" fontSize="0.875rem">
          {article.author && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="caption">{article.author}</Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Visibility sx={{ fontSize: 16 }} />
            <Typography variant="caption">{article.view_count || 0}</Typography>
          </Box>
          <Typography variant="caption">
            {article.published_at ? formatDistanceToNow(new Date(article.published_at), { addSuffix: true }) : 'Recently'}
          </Typography>
        </Box>
      </CardContent>
      <CardActions>
        <Button 
          size="small" 
          startIcon={<Visibility />} 
          onClick={() => setPreviewNews(article)}
        >
          View
        </Button>
        <Button 
          size="small" 
          startIcon={<Edit />} 
          onClick={() => { setSelectedNews(article); setMode('edit'); }}
        >
          Edit
        </Button>
        <IconButton 
          size="small" 
          color="error" 
          onClick={() => setDeleteConfirm(article)}
        >
          <Delete />
        </IconButton>
      </CardActions>
    </Card>
  );

  return (
    <Box>
      {mode === 'list' ? (
        <>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" fontWeight="bold">News Management</Typography>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<Add />} 
              onClick={() => setMode('create')}
              sx={{ borderRadius: 2 }}
            >
              Create News
            </Button>
          </Box>

          {/* Search and Filters */}
          <Box mb={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search news..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="Category"
                    sx={{ borderRadius: 2 }}
                  >
                    {categoryTabs.map(cat => (
                      <MenuItem key={cat.value} value={cat.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {cat.icon}
                          {cat.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort By"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="latest">Latest First</MenuItem>
                    <MenuItem value="oldest">Oldest First</MenuItem>
                    <MenuItem value="views">Most Viewed</MenuItem>
                    <MenuItem value="title">Title A-Z</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Time Period</InputLabel>
                  <Select
                    value={dayFilter}
                    onChange={(e) => setDayFilter(e.target.value)}
                    label="Time Period"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="yesterday">Yesterday</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs */}
          <Box mb={3}>
            <Tabs 
              value={activeTab} 
              onChange={(e, v) => { 
                setActiveTab(v); 
                setStatusFilter(tabs[v].value); 
              }}
              sx={{ 
                '& .MuiTab-root': { 
                  minHeight: 48,
                  textTransform: 'none',
                  fontWeight: 500
                }
              }}
            >
              {tabs.map((tab, idx) => (
                <Tab 
                  key={idx} 
                  label={tab.label} 
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          {/* News Grid */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>Loading news...</Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredNews.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <NewsCard article={article} />
                </Grid>
              ))}
            </Grid>
          )}

          {filteredNews.length === 0 && (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">
                No news articles found
              </Typography>
            </Box>
          )}
        </>
      ) : mode === 'create' ? (
        <CreateNewsForm 
          onBack={() => setMode('list')} 
          onSuccess={() => setMode('list')} 
        />
      ) : (
        <EditNewsForm 
          news={selectedNews} 
          onBack={() => setMode('list')} 
          onSuccess={() => setMode('list')} 
        />
      )}

      {/* Preview Dialog */}
      <Dialog 
        open={!!previewNews} 
        onClose={() => setPreviewNews(null)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Preview News Article</DialogTitle>
        <DialogContent>
          {previewNews && <NewsPreview news={previewNews} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewNews(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog 
        open={!!deleteConfirm} 
        onClose={() => setDeleteConfirm(null)}
      >
        <DialogTitle>Delete News Article</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button 
            color="error" 
            onClick={() => handleDelete(deleteConfirm.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminNewsPage;
